import React, { useState, useRef, useEffect, ClipboardEvent } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { HotkeysProvider } from '@blueprintjs/core';
import { CellProps, Column, FocusedCellCoordinates, Region, Table2 } from '@blueprintjs/table';
import TextAreaCell from '../TextAreaCell';
import ResizableDiv from '../ResizableDiv';
import SpinnerIcon from '@components/atoms/SpinnerIcon';
import clsx from 'clsx';
import { useScrollbarWidth } from '../../utils';
import {
    fetchSequencesByIds,
    parseClipboardData,
    IFetchTableRow,
    updateFetchModalRows,
    convertRowsToSaveFormat,
    calculateFetchModalTableDimensions,
    extractRowsWithIdsOnly,
} from './utils';

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (
        sequences: Record<number, Record<number, string>>,
        modalIndexToOriginalIndex: Record<number, number>,
    ) => void;
    sequenceRetrievalAppUri: string;
    existingSequenceRows?: Record<number, Record<number, string>>;
}

export default function FetchIdsModal(props: IProps) {
    const [rows, setRows] = useState<Record<number, IFetchTableRow>>({});
    const [modalIndexToOriginalIndex, setModalIndexToOriginalIndex] = useState<Record<number, number>>({});
    const [focusedCell, setFocusedCell] = useState<FocusedCellCoordinates>({ row: 0, col: 0, focusSelectionIndex: 0 });
    const [selectedRegions, setSelectedRegions] = useState<Region[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sequenceRetrievalErrorMessage, setSequenceRetrievalErrorMessage] = useState<string | null>(null);
    const [detailedErrorOutput, setDetailedErrorOutput] = useState<string | null>(null);
    const [showDetailedError, setShowDetailedError] = useState(false);
    const tableRef = useRef<Table2>();
    const primaryButtonRef = useRef<HTMLButtonElement>();
    const [tableRenderInvalidator, setTableRenderInvalidator] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>();
    const [containerWidth, setContainerWidth] = useState<number | null>(null);
    const scrollbarWidth = useScrollbarWidth() + 5;

    const { columnWidths, rowCount } = calculateFetchModalTableDimensions(rows, containerWidth, scrollbarWidth);

    useEffect(() => {
        if (props.isOpen) {
            if (props.existingSequenceRows) {
                const rowsWithIdsOnly = extractRowsWithIdsOnly(props.existingSequenceRows);
                const originalIndices = Object.keys(rowsWithIdsOnly).map(Number);
                if (originalIndices.length > 0) {
                    // Remap to sequential indices starting from 0
                    const remappedRows: Record<number, IFetchTableRow> = {};
                    const indexMapping: Record<number, number> = {};
                    originalIndices.forEach((originalIndex, modalIndex) => {
                        remappedRows[modalIndex] = rowsWithIdsOnly[originalIndex];
                        indexMapping[modalIndex] = originalIndex;
                    });
                    setRows(remappedRows);
                    setModalIndexToOriginalIndex(indexMapping);
                } else {
                    setRows({ 0: { id: '', sequence: '' } });
                    setModalIndexToOriginalIndex({});
                }
            } else {
                setRows({ 0: { id: '', sequence: '' } });
                setModalIndexToOriginalIndex({});
            }

            setFocusedCell({ row: 0, col: 0, focusSelectionIndex: 0 });
            setSelectedRegions([{ rows: [0, 0], cols: [0, 0] }]);
            setIsLoading(false);
            setSequenceRetrievalErrorMessage(null);
            setDetailedErrorOutput(null);
            setShowDetailedError(false);
            setTimeout(() => {
                if (primaryButtonRef.current) {
                    primaryButtonRef.current.focus();
                }
            }, 100);
        }
    }, [props.isOpen, props.existingSequenceRows]);

    useEffect(() => {
        const onResize = () => {
            if (containerRef.current) {
                const newWidth = Math.max(containerRef.current.offsetWidth, 100);
                setContainerWidth(newWidth);
            }
        };

        // Use a timeout to ensure the modal is fully rendered
        const timer = setTimeout(onResize, 100);

        onResize();
        window.addEventListener('resize', onResize);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', onResize);
        };
    }, [props.isOpen]);

    const updateRows = (newRows: Record<number, Partial<IFetchTableRow>>) => {
        setRows((prevRows) => updateFetchModalRows(prevRows, newRows));
        setTableRenderInvalidator(!tableRenderInvalidator);
    };

    const cellRenderer = (rowIndex: number, columnIndex: number): React.ReactElement<CellProps> => {
        const row = rows[rowIndex] || { id: '', sequence: '' };
        const value = columnIndex === 0 ? row.id : row.sequence;
        const isSequenceColumn = columnIndex === 1;

        return (
            <TextAreaCell
                value={value}
                onFocus={() => {}}
                setValue={(newValue) => {
                    if (columnIndex === 0) {
                        updateRows({ [rowIndex]: { id: newValue } });
                    }
                }}
                readOnly={isSequenceColumn}
            />
        );
    };

    const handlePaste = (event: ClipboardEvent<HTMLDivElement>): void => {
        const rawText = event.clipboardData.getData('Text').trim();
        if (!focusedCell || focusedCell.col !== 0) {
            return;
        }

        event.preventDefault();
        const { rowsToUpdate } = parseClipboardData(rawText, focusedCell.row);
        updateRows(rowsToUpdate);
    };

    const handleFetch = async () => {
        setIsLoading(true);
        setSequenceRetrievalErrorMessage(null);
        setDetailedErrorOutput(null);
        setShowDetailedError(false);

        const { updatedRows, errorMessage, detailedOutput } = await fetchSequencesByIds(
            props.sequenceRetrievalAppUri,
            rows,
        );
        updateRows(updatedRows);

        if (errorMessage) {
            setSequenceRetrievalErrorMessage(errorMessage);
            setDetailedErrorOutput(detailedOutput);
        }
        setIsLoading(false);
    };

    const handleSave = () => {
        const sequencesToSave = convertRowsToSaveFormat(rows);
        props.onSave(sequencesToSave, modalIndexToOriginalIndex);
        props.onClose();
    };

    const columnNames = ['IDs', 'Sequence'];

    return (
        <Dialog open={props.isOpen} onClose={() => {}} className='relative z-10'>
            <DialogBackdrop
                transition
                className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in'
            />
            <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
                <div className='flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'>
                    <DialogPanel
                        transition
                        className='relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95'
                    >
                        <div className='absolute right-0 top-0 hidden pr-4 pt-4 sm:block'>
                            <button
                                type='button'
                                onClick={props.onClose}
                                className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-800 focus:ring-offset-2'
                            >
                                <span className='sr-only'>Close</span>
                                <XMarkIcon aria-hidden='true' className='h-6 w-6' />
                            </button>
                        </div>
                        <div>
                            <DialogTitle as='h3' className='text-base font-semibold text-gray-900'>
                                Fetch Sequences by ID
                            </DialogTitle>
                            <p className='text-sm text-gray-500 mt-2'>
                                Enter sequence IDs in the first column. You can paste multiple IDs from Excel. The
                                sequence column will be populated after fetching.
                            </p>
                        </div>

                        <div className='mt-4'>
                            <div
                                className='w-full rounded-md overflow-hidden border border-gray-300 relative'
                                onPasteCapture={handlePaste}
                            >
                                {isLoading && (
                                    <div className='absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10'>
                                        <SpinnerIcon className='h-8 w-8 text-cyan-800' />
                                    </div>
                                )}
                                <ResizableDiv initialHeight={300} minHeight={200}>
                                    <div ref={containerRef} className='w-full h-full'>
                                        <HotkeysProvider>
                                            <Table2
                                                cellRendererDependencies={[tableRenderInvalidator]}
                                                columnWidths={columnWidths}
                                                enableColumnHeader={true}
                                                enableColumnResizing={false}
                                                enableFocusedCell={true}
                                                enableRowHeader={false}
                                                focusedCell={focusedCell}
                                                key={!containerWidth ? 'loading' : 'loaded'}
                                                numRows={rowCount}
                                                onFocusedCell={setFocusedCell}
                                                onSelection={setSelectedRegions}
                                                ref={tableRef}
                                                selectedRegions={selectedRegions}
                                            >
                                                {columnNames.map((name, index) => (
                                                    <Column
                                                        key={index}
                                                        name=''
                                                        cellRenderer={cellRenderer}
                                                        nameRenderer={() => (
                                                            <div className='flex items-center'>
                                                                <span
                                                                    style={{ marginLeft: -2, fontSize: 12 }}
                                                                    className='text-gray-600 font-bold overflow-hidden text-ellipsis whitespace-nowrap'
                                                                >
                                                                    {name}
                                                                </span>
                                                            </div>
                                                        )}
                                                    />
                                                ))}
                                            </Table2>
                                        </HotkeysProvider>
                                    </div>
                                </ResizableDiv>
                            </div>
                        </div>

                        {sequenceRetrievalErrorMessage && (
                            <div className='mt-3 text-sm text-red-600'>
                                <div className='mb-2'>
                                    <div className='font-medium'>Error:</div>
                                    <div className='mt-1 text-xs bg-red-50 p-2 rounded border'>
                                        {sequenceRetrievalErrorMessage}
                                    </div>
                                    {detailedErrorOutput && (
                                        <div className='mt-2'>
                                            <button
                                                onClick={() => setShowDetailedError(!showDetailedError)}
                                                className='text-cyan-800 hover:text-cyan-700 underline text-xs'
                                            >
                                                {showDetailedError ? 'Hide' : 'Show'} detailed error information
                                            </button>

                                            {showDetailedError && (
                                                <div className='mt-2 text-xs font-mono bg-red-50 p-3 rounded border max-h-40 overflow-y-auto whitespace-pre-wrap'>
                                                    {detailedErrorOutput}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className='mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-3 sm:gap-3'>
                            <button
                                onClick={handleFetch}
                                disabled={isLoading || !Object.values(rows).some((row) => row.id && row.id.trim())}
                                ref={primaryButtonRef}
                                className='inline-flex w-full justify-center rounded-md bg-cyan-800 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-cyan-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-800 sm:col-start-2 disabled:opacity-50'
                                type='button'
                            >
                                {isLoading ? 'Fetching...' : 'Fetch'}
                            </button>

                            {!isLoading && Object.values(rows).some((row) => row.sequence && row.sequence.trim()) && (
                                <button
                                    onClick={handleSave}
                                    className='inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 sm:col-start-3'
                                    type='button'
                                >
                                    {sequenceRetrievalErrorMessage ? 'Save Found Sequences' : 'Save'}
                                </button>
                            )}

                            <button
                                className='mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-100 sm:col-start-1 sm:mt-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-800'
                                onClick={props.onClose}
                                type='button'
                            >
                                Cancel
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
